import { ReactNode, useId } from 'react'
import { useT } from '_metronic/i18n/Metronici18n'
import { formatarContagem, formatarPercentual } from '../../core/formatacao'
import { AjudaIndicador, montarNotasAjuda } from '../ajuda/AjudaIndicador'
import { IconPaths, KTSVG } from '_metronic/helpers'

/** Um denominador (ou numerador) escrito por extenso ao lado do indicador. */
export type ValorNomeado = {
  rotulo: string
  valor: number
}

type Props = {
  /** Nome curto do indicador (vem do pt-br.json, é chrome de tela). */
  titulo: string
  /** Fração 0..1 do contrato; `null` é denominador zero, nunca `0`. */
  valor: number | null
  numerador: ValorNomeado
  denominador: ValorNomeado
  /** Contagens que o contrato expõe ao lado da taxa (o que ficou fora do denominador, retomadas, etc.). */
  extras?: ValorNomeado[]
  /** Notas metodológicas vindas da API (`rotulos.*`): vão para o `?` do cabeçalho, omitidas quando vazias. */
  notas?: (string | undefined)[]
  children?: ReactNode
}

/**
 * Card de taxa da família de qualidade. Irmão do `IndicadorCard` da família
 * operacional, não uma generalização dele: aquele formata minutos e mediana,
 * este formata fração e tem denominador plural. A casca visual e o padrão de
 * a11y (grupo rotulado, nota ancorada por `aria-describedby`, estado sem dados
 * com texto e não só com ícone) são os mesmos, replicados de propósito.
 *
 * Numerador e denominador continuam visíveis mesmo quando a taxa é `null`: é o
 * denominador que explica por que não há taxa.
 */
export const CardTaxaQualidade = ({
  titulo,
  valor,
  numerador,
  denominador,
  extras,
  notas,
  children,
}: Props) => {
  const { t } = useT()
  const idBase = useId()
  const idTitulo = `${idBase}-titulo`

  const taxa = formatarPercentual(valor)
  const notasAjuda = montarNotasAjuda(idBase, notas ?? [])
  const idsNotas = notasAjuda.map((nota) => nota.id)

  const itens: ValorNomeado[] = [numerador, denominador, ...(extras ?? [])]

  return (
    <div
      className='card card-flush h-100 border border-gray-300'
      role='group'
      aria-labelledby={idTitulo}
      aria-describedby={idsNotas.length > 0 ? idsNotas.join(' ') : undefined}
    >
      <div className='card-body d-flex flex-column p-6'>
        <div className='d-flex justify-content-between align-items-start gap-2 mb-3'>
          <h4 id={idTitulo} className='fs-6 fw-semibold text-gray-700 mb-0'>
            {titulo}
          </h4>
          <AjudaIndicador rotulo={t('indicadores.ajuda.rotulo', { titulo })} notas={notasAjuda} />
        </div>

        {taxa === null ? (
          <p className='d-flex align-items-center text-gray-700 fs-6 mb-3'>
            <KTSVG path={IconPaths.CircleMinus} className='svg-icon-em me-2 fs-5' ariaHidden />
            <span>{t('indicadores.card.sem_dados')}</span>
          </p>
        ) : (
          <p className='fs-2hx fw-bold text-gray-900 mb-3'>{taxa}</p>
        )}

        <dl className='row mb-0'>
          {itens.map((item) => (
            <div key={item.rotulo} className='col-12 d-flex justify-content-between gap-3 mb-1'>
              <dt className='fs-7 fw-normal text-gray-700'>{item.rotulo}</dt>
              <dd className='fs-7 fw-semibold text-gray-900 mb-0'>
                {formatarContagem(item.valor)}
              </dd>
            </div>
          ))}
        </dl>

        {children}
      </div>
    </div>
  )
}
